/**
 * Calendar and contacts calls: `/api/calendar/*` and `/api/contacts/*` on this
 * origin, served by the BFF over CalDAV/CardDAV with the user's own token.
 */

import type {
  CalendarEvent,
  CalendarInfo,
  Contact,
  ContactInput,
  EventInput,
  FreeBusy,
} from 'zitimail-shared-types';

import { fetchOrExplain, parseJson, plainFailure } from './http';
import { MailError } from './mail';

async function call<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const response = await fetchOrExplain(
    path,
    {
      method: init.method ?? 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'X-Genworx-Request': '1',
        ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    },
    'calendar service',
  );
  const payload = parseJson(await response.text());
  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new Event('genworx:session-ended'));
    }
    const message = (payload as { message?: string } | undefined)?.message;
    throw new MailError(
      response.status,
      message ?? plainFailure('calendar service', response.status),
    );
  }
  // A change to the calendar: the reminders read it again (EventReminders).
  if ((init.method ?? 'GET') !== 'GET' && !path.startsWith('/api/contacts')) {
    window.dispatchEvent(new Event('genworx:calendar-changed'));
  }
  return payload as T;
}

export const calendar = {
  calendars: () => call<CalendarInfo[]>('/api/calendar/calendars'),
  createCalendar: (name: string, color: string) =>
    call<CalendarInfo>('/api/calendar/calendars', { method: 'POST', body: { name, color } }),
  events: (start: Date, end: Date) =>
    call<CalendarEvent[]>(
      `/api/calendar/events?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`,
    ),
  create: (event: EventInput) =>
    call<{ uid: string; href: string }>('/api/calendar/events', { method: 'POST', body: event }),
  /** With `recurrenceId`, changes that one occurrence of a series. */
  update: (href: string, etag: string, event: EventInput, recurrenceId?: string) =>
    call('/api/calendar/events', {
      method: 'PUT',
      body: { href, etag, event, ...(recurrenceId ? { recurrenceId } : {}) },
    }),
  setAppearance: (href: string, change: { name?: string; color?: string }) =>
    call<CalendarInfo>('/api/calendar/calendars/appearance', {
      method: 'POST',
      body: { href, ...change },
    }),
  remove: (href: string, etag: string, recurrenceId?: string) =>
    call('/api/calendar/events/delete', {
      method: 'POST',
      body: { href, etag, ...(recurrenceId ? { recurrenceId } : {}) },
    }),
  freeBusy: (emails: readonly string[], start: Date, end: Date) => {
    const q = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
    emails.forEach((e) => q.append('email', e));
    return call<FreeBusy[]>(`/api/calendar/freebusy?${q.toString()}`);
  },
  applyReply: (folder: string, uid: string) =>
    call('/api/mail/apply-reply', { method: 'POST', body: { folder, uid: Number(uid) } }),
  rsvp: (folder: string, uid: string, answer: 'accept' | 'tentative' | 'decline') =>
    call('/api/mail/rsvp', { method: 'POST', body: { folder, uid: Number(uid), answer } }),
};

export const contacts = {
  list: () => call<Contact[]>('/api/contacts'),
  search: (q: string) =>
    call<Array<{ name: string; email: string; source: 'contacts' | 'directory' }>>(
      `/api/contacts/search?q=${encodeURIComponent(q)}`,
    ),
  directory: (q: string) =>
    call<Array<{ name: string; email: string }>>(
      `/api/contacts/directory?q=${encodeURIComponent(q)}`,
    ),
  create: (contact: ContactInput) => call('/api/contacts', { method: 'POST', body: { contact } }),
  update: (href: string, etag: string, contact: ContactInput) =>
    call('/api/contacts', { method: 'PUT', body: { href, etag, contact } }),
  remove: (href: string, etag: string) =>
    call('/api/contacts/delete', { method: 'POST', body: { href, etag } }),
};
